const generateSrcset = (image, widths = []) => {
  const imageUrl = new URL(image["src"], document.baseURI);
  if (!/^https?:$/.test(imageUrl.protocol)) return "";
  return widths
    .filter((width) => width <= image["width"])
    .map((width) => {
      imageUrl.searchParams.set("width", width.toString());
      return `${imageUrl.href} ${width}w`;
    })
    .join(", ");
};

const createImageElement = (image, classes, sizes, productTitle) => {
  const previewImage = image["preview_image"];
  const source = new URL(previewImage["src"], document.baseURI);
  if (!/^https?:$/.test(source.protocol)) throw new Error("Invalid product image URL");
  const newImage = new Image(previewImage["width"], previewImage["height"]);
  newImage.className = classes;
  newImage.alt = image["alt"] || productTitle;
  newImage.sizes = sizes;
  newImage.srcset = generateSrcset(
    previewImage,
    [165, 360, 533, 720, 940, 1066]
  );
  newImage.loading = "eager";
  newImage.src = source.href;
  return newImage;
};

const cardSwatchSelections = new WeakMap();
const boundCardSwatches = new WeakSet();

const waitForCardImage = (image) => {
  if (typeof image.decode === "function") return image.decode();
  if (image.complete) {
    return image.naturalWidth > 0 ? Promise.resolve() : Promise.reject();
  }
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      image.removeEventListener("load", onLoad);
      image.removeEventListener("error", onError);
    };
    const onLoad = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject();
    };
    image.addEventListener("load", onLoad);
    image.addEventListener("error", onError);
  });
};

const preserveCardImageFit = (source, destination) => {
  const style = window.getComputedStyle(source);
  destination.style.objectFit = style.objectFit;
  destination.style.objectPosition = style.objectPosition;
};

const cardMoney = (cents) => {
  const format = window.theme?.moneyFormat || "${{amount}}";
  const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || "amount";
  const decimals = token.includes("no_decimals") ? 0 : 2;
  const decimal = /comma_separator|with_space_separator/.test(token) ? "," : ".";
  const thousands = token.includes("space_separator") ? " " :
    token.includes("apostrophe_separator") ? "'" : decimal === "," ? "." : ",";
  const [whole, fraction] = (Number(cents) / 100).toFixed(decimals).split(".");
  const amount = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
    (fraction ? decimal + fraction : "");
  const text = document.createElement("span");
  // The format comes from this store's theme settings; discard any money wrapper.
  text.innerHTML = format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  return text.textContent;
};

const updateCardVariantDetails = (wrapper, input, variant) => {
  const url = new URL(input.dataset.variantLink ||
    `${window.Shopify.routes.root}products/${wrapper.dataset.product}`, document.baseURI);
  if (!/^https?:$/.test(url.protocol)) return;
  url.searchParams.set("variant", String(variant.id));
  const href = url.origin === new URL(document.baseURI).origin ?
    `${url.pathname}${url.search}${url.hash}` : url.href;
  wrapper.querySelectorAll(".card__title a, .js-color-swatches-link").forEach(link => {
    link.setAttribute("href", href);
  });
  wrapper.querySelectorAll("modal-opener button[data-product-url]").forEach(button => {
    button.dataset.productUrl = href;
  });
  wrapper.querySelectorAll('.card__add-to-cart input[name="id"]').forEach(field => {
    field.value = variant.id;
  });
  wrapper.querySelectorAll('.card__add-to-cart button[name="add"]').forEach(button => {
    button.disabled = !variant.available;
    button.setAttribute("aria-disabled", String(!variant.available));
    button.querySelector(":scope > span")?.classList.toggle("hidden", !variant.available);
    button.querySelector(".sold-out-message")?.classList.toggle("hidden", variant.available);
  });
  const price = wrapper.querySelector(".card-information .price--card");
  if (price) {
    const sale = Number(variant.compare_at_price) > Number(variant.price);
    price.classList.toggle("price--on-sale", sale);
    price.classList.toggle("price--sold-out", !variant.available);
    price.classList.remove("price--no-compare", "price--unavailable");
    price.querySelectorAll(".price__regular .price-item--regular, .price-item--sale").forEach(item => {
      const amount = document.createElement("span");
      amount.className = "price__amount";
      amount.textContent = cardMoney(variant.price);
      item.replaceChildren(amount);
    });
    const compare = price.querySelector(".price__card-original .price__compare");
    if (compare) compare.textContent = cardMoney(variant.compare_at_price || 0);
    const badge = price.querySelector(".card-information__sale-badge");
    if (badge) {
      const percentage = sale ? Math.floor((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
      badge.textContent = badge.textContent.replace(/\d+(?:[.,]\d+)?/, String(percentage));
      badge.hidden = !sale || !variant.available || percentage <= 0;
    }
    price.querySelector(".unit-price")?.classList.add("hidden");
  }
  wrapper.querySelectorAll(".card__badge .badge--soldout").forEach(badge => {
    badge.hidden = variant.available;
  });
  input.classList.toggle("disabled", !variant.available);
};

const checkSwatches = () => {
  document.querySelectorAll(".js-color-swatches-wrapper").forEach((wrapper) => {
    wrapper.querySelectorAll(".js-color-swatches input").forEach((input) => {
      if (boundCardSwatches.has(input)) return;
      boundCardSwatches.add(input);
      input.addEventListener("change", (event) => {
        const primaryImage = wrapper.querySelector(".media--first");
        const secondaryImage = wrapper.querySelector(".media--second");
        const handleProduct = wrapper.dataset.product;

        if (event.currentTarget.checked) {
          const selection = {};
          cardSwatchSelections.set(wrapper, selection);
          const isCurrentSelection = () =>
            cardSwatchSelections.get(wrapper) === selection && wrapper.isConnected;
          const currentColor = event.currentTarget.value;
          const variantId = event.currentTarget.dataset.variantId;
          const optionIndex =
            Number(input.closest("[data-option-position]")?.dataset.optionPosition) - 1;

          const applyProduct = async (product) => {
              if (!isCurrentSelection() || !Array.isArray(product?.variants)) return;
              const matchesOption = (item) =>
                Array.isArray(item?.options) && (Number.isInteger(optionIndex) && optionIndex >= 0
                  ? item.options[optionIndex] === currentColor
                  : item.options.includes(currentColor));
              const variant = product.variants.find(item =>
                (variantId ? String(item.id) === variantId : true) && matchesOption(item));

              if (!variant || !variant.id || typeof variant.available !== "boolean" ||
                  !Number.isFinite(variant.price) || variant.price < 0) return;
              updateCardVariantDetails(wrapper, input, variant);
              if (!primaryImage || !variant.featured_media) return;
              try {
                const newPrimaryImage = createImageElement(
                  variant["featured_media"],
                  primaryImage.className,
                  primaryImage.sizes,
                  product.title
                );

                if (newPrimaryImage.src === primaryImage.src) return;
                const imageToShow =
                  secondaryImage &&
                  new URL(secondaryImage.src).pathname ===
                    new URL(newPrimaryImage.src).pathname
                    ? secondaryImage
                    : newPrimaryImage;
                imageToShow.loading = "eager";
                await waitForCardImage(imageToShow);
                if (!isCurrentSelection()) return;

                const currentPrimaryImage = wrapper.querySelector(".media--first");
                if (!currentPrimaryImage) return;
                // Keep the existing framing, and keep its image visible until the next is ready.
                preserveCardImageFit(currentPrimaryImage, imageToShow);
                if (imageToShow === secondaryImage) {
                  currentPrimaryImage.remove();
                  secondaryImage.classList.remove("media--second");
                  secondaryImage.classList.add("media--first");
                } else {
                  currentPrimaryImage.replaceWith(newPrimaryImage);
                  if (secondaryImage) secondaryImage.remove();
                }
              } catch {
                // A failed image leaves the current product picture in place.
              }
          };
          // Card Liquid already supplies the selected product's variants. Use
          // that snapshot for a synchronous, consistent color/price/link update.
          // Keep the network fallback for cards without a complete inline match.
          let inlineVariants;
          try {
            inlineVariants = JSON.parse(input.closest("variant-radios")
              ?.querySelector("[data-all-variants-no-high]")?.textContent || "null");
          } catch {}
          if (Array.isArray(inlineVariants) && inlineVariants.some(item => String(item.id) === variantId)) {
            applyProduct({variants:inlineVariants, title:primaryImage?.alt || ""});
          } else {
            wrapper.querySelectorAll('.card__add-to-cart button[name="add"]').forEach(button => {
              button.disabled = true;
              button.setAttribute("aria-disabled", "true");
            });
            jQuery.getJSON(window.Shopify.routes.root + `products/${handleProduct}.js`, applyProduct);
          }
        }
      });
    });
  });
};

function colorSwatches() {
  checkSwatches();

  document.addEventListener("shopify:section:load", checkSwatches);
}

(function () {
  colorSwatches();
})();
